<template>
    <div class="container-fluid py-3">
        <div class="row mb-3">
            <div class="col-3">
                <form method="post" @submit.prevent="onConnectLedger">
                    <label for="accountIndex">Account Index: (m/44'/60'/0'/0/x)</label>
                    <input type="number" min="0" class="form-control" id="accountIndex" v-model.number="accountIndex" />
                    <button type="submit" class="btn btn-matic mt-1 w-100">Load from Ledger</button>
                </form>
            </div>
            <div class="col-4">
                <label>Address:</label>
                <p class="bold text-break">{{ maticAddress }}</p>
            </div>
            <div class="col-3">
                <label>HD Path:</label>
                <p class="bold">{{ hdPath }}</p>
            </div>
            <div class="col-2">
                <button class="btn btn-secondary btn-sm mt-3 mr-1" @click="logout">Logout</button>
                <button class="btn btn-matic btn-sm mt-3" @click="showAddAddressModal = true">Add Address</button>
                <button class="btn btn-outline-secondary btn-sm mt-2 w-100" @click="showReceive = true" v-if="maticAddress !== 'N/A'">📥 Receive</button>
            </div>
        </div>

        <div class="row">
            <div class="col-sm-7">
                <div class="total-usdt-banner" v-if="balances.length">
                    <span class="tub-label">Total Value</span>
                    <span class="tub-value">{{ totalUsdtStr }}</span>
                    <button class="tub-refresh" @click="loadBalances" :disabled="tokensLoading" title="Refresh USDT value">
                        <span v-if="tokensLoading" class="spinner-border spinner-border-sm" role="status"></span>
                        <span v-else>↻</span>
                    </button>
                </div>
                <table class="table table-striped">
                    <thead>
                        <tr><th>Asset</th><th>Balance</th><th>Value (USDT)</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                        <tr v-if="balances.length === 0 && !tokensLoading">
                            <td colspan="4" class="text-center text-muted">Connect Ledger to load balances</td>
                        </tr>
                        <tr v-if="tokensLoading">
                            <td colspan="4" class="text-center text-muted small py-1">
                                <span class="spinner-border spinner-border-sm mr-1" style="width:.75rem;height:.75rem"></span>
                                {{ pricingProgressMsg }}
                            </td>
                        </tr>
                        <tr v-for="b in sortedBalances" :key="b.contractAddress || b.ticker">
                            <td><span class="ticker-badge">{{ b.ticker }}</span></td>
                            <td>{{ b.balance }}</td>
                            <td>{{ b.usdValue != null ? '$' + b.usdValue.toFixed(2) : priceOf(b.ticker, b.rawBalance) }}</td>
                            <td>
                                <button class="btn btn-success btn-sm" @click="openModal(b.ticker, b.balance, b.type, b.contractAddress, b.decimals)">Send</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="col-sm-5">
                <table class="table table-striped text-center">
                    <thead><tr><th>Name</th><th>Address</th><th>Actions</th></tr></thead>
                    <tbody>
                        <tr v-if="addresses.length === 0"><td colspan="3" class="text-muted">No saved addresses</td></tr>
                        <tr v-for="(entry, i) in addresses" :key="i">
                            <td>{{ entry.name }}</td>
                            <td class="small text-break">{{ entry.address }}</td>
                            <td>
                                <button class="btn btn-info btn-sm mx-1" @click="copyAddress(entry.address)">Copy</button>
                                <button class="btn btn-danger btn-sm" @click="deleteAddress(i)">Del</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div class="row mt-2">
            <div class="col-12">
                <div class="rpc-panel">
                    <span class="rpc-title">RPC Nodes</span>
                    <span v-for="rpc in rpcList" :key="rpc.url" class="rpc-item" :class="{ 'rpc-active': activeRpc === rpc.url }" @click="switchRpc(rpc.url)">
                        <span class="rpc-dot" :class="rpcStatus[rpc.url]"></span>{{ rpc.label }}
                    </span>
                    <span class="rpc-item" @click="pingAllRpc" style="cursor:pointer;margin-left:auto;">↻ Refresh</span>
                </div>
            </div>
        </div>

        <div class="row mt-4">
            <div class="col-12">
                <div class="chart-panel">
                    <div class="chart-toolbar">
                        <span class="chart-panel-title">Transaction History</span>
                        <div class="chart-input-group">
                            <input type="text" class="form-control form-control-sm chart-addr-input" placeholder="Polygon address (0x…)" v-model="chartAddress" @keyup.enter="loadChart" />
                            <button class="btn btn-matic btn-sm" @click="loadChart" :disabled="chartLoading">
                                <span v-if="chartLoading">Loading…</span><span v-else>Load Chart</span>
                            </button>
                            <button v-if="maticAddress !== 'N/A'" class="btn btn-outline-secondary btn-sm" @click="chartAddress = maticAddress; loadChart()">My Address</button>
                        </div>
                        <div class="chart-toggles">
                            <button class="btn btn-sm" :class="chartMode==='count' ? 'btn-matic' : 'btn-outline-secondary'" @click="setChartMode('count')">TX Count</button>
                            <button class="btn btn-sm" :class="chartMode==='volume' ? 'btn-matic' : 'btn-outline-secondary'" @click="setChartMode('volume')">MATIC Volume</button>
                        </div>
                        <div class="chart-toggles">
                            <button v-for="d in [30,60,90]" :key="d" class="btn btn-sm" :class="chartDays===d ? 'btn-matic' : 'btn-outline-secondary'" @click="chartDays=d; renderChart()">{{ d }}d</button>
                        </div>
                    </div>
                    <div class="chart-summary" v-if="chartStats.total > 0">
                        <span class="chart-stat"><strong>{{ chartStats.total }}</strong> TXs</span>
                        <span class="chart-stat"><span class="stat-in">▲ {{ chartStats.received }} MATIC received</span></span>
                        <span class="chart-stat"><span class="stat-out">▼ {{ chartStats.sent }} MATIC sent</span></span>
                    </div>
                    <div class="chart-wrapper">
                        <canvas ref="txChart"></canvas>
                        <div v-if="!chartLoaded && !chartLoading" class="chart-placeholder">Enter a Polygon address above</div>
                        <div v-if="chartLoading" class="chart-placeholder">
                            <div class="spinner-border" style="color:#8247E5" role="status"></div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <Modal v-model="showReceipt" title="Transaction Sent">
            <div class="text-center p-3">
                <div style="font-size:3rem;color:#28a745">✓</div>
                <p class="mt-2">Transaction submitted to Polygon network.</p>
                <input type="text" class="form-control form-control-sm mb-2" :value="txid" readonly />
                <a :href="'https://polygonscan.com/tx/' + txid" target="_blank" class="btn btn-matic btn-sm">View on PolygonScan ↗</a>
            </div>
        </Modal>

        <Modal v-model="showModal" :title="'Send ' + modalTicker">
            <form novalidate @submit.prevent="sendTransaction">
                <div class="form-group">
                    <label>From</label><input type="text" class="form-control form-control-sm text-muted" :value="maticAddress" readonly />
                </div>
                <div class="form-group">
                    <label>To Address</label><input type="text" class="form-control" placeholder="0x..." v-model="modalToAddress" />
                </div>
                <div class="form-group">
                    <label>Amount ({{ modalTicker }})</label>
                    <div class="input-group">
                        <input type="text" class="form-control" v-model="modalAmount" />
                        <div class="input-group-append"><button type="button" class="btn btn-outline-secondary btn-sm" @click="modalAmount = modalMaxBalance">Max</button></div>
                    </div>
                </div>
                <div class="form-group">
                    <label>Gas Price (Gwei)</label><input type="number" class="form-control" v-model.number="gasPrice" min="1" />
                </div>
                <div class="form-group">
                    <label>Gas Limit</label><input type="number" class="form-control" v-model.number="gasLimit" min="21000" />
                </div>
                <div class="form-group small text-muted">Estimated fee: <strong>{{ estimatedFee }} MATIC</strong></div>
                <div class="row modal-footer">
                    <div class="col-12">
                        <button class="btn btn-danger float-left" type="button" @click="showModal = false">Cancel</button>
                        <button class="btn btn-matic float-right" type="submit" :disabled="sending">
                            <span v-if="sending">Sending…</span><span v-else>Send</span>
                        </button>
                    </div>
                </div>
            </form>
        </Modal>

        <Modal v-model="showAddAddressModal" title="Add New Address">
            <form novalidate @submit.prevent="submitNewAddress">
                <div class="form-group"><label>Name</label><input type="text" class="form-control" placeholder="Label" v-model="newAddressName" /></div>
                <div class="form-group"><label>Polygon Address</label><input type="text" class="form-control" placeholder="0x..." v-model="newAddress" /></div>
                <div class="row modal-footer">
                    <div class="col-12">
                        <button type="button" class="btn btn-danger float-left" @click="showAddAddressModal = false">Cancel</button>
                        <button type="submit" class="btn btn-matic float-right">Save</button>
                    </div>
                </div>
            </form>
        </Modal>

        <ReceiveModal v-if="showReceive" :address="maticAddress" chain="polygon" @close="showReceive = false" />

        <!-- Transaction History -->

        <!-- Address Matrix / Tree -->
        <div class="row mt-4">
            <div class="col-12">
                <div class="chart-panel">
                    <div class="chart-panel-title mb-2">Address Overview</div>
                    <AddressMatrix
                        :cells="matrixCells"
                        :active-index="accountIndex"
                        accent-color="#8247E5"
                        unit="MATIC"
                        :decimals="6"
                        :loading="matrixLoading"
                        root-label="m/44'/60'/0'/0"
                        @refresh="loadMatrix(true)"
                        @select="onMatrixSelect" />
                </div>
            </div>
        </div>

        <WalletTxHistory chain="polygon" :address="maticAddress" accent-color="#8247E5" />
    </div>
</template>

<script>
import Eth from '@ledgerhq/hw-app-eth';
import { Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend, BarController } from 'chart.js';
Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, BarController);
import TransportWebHID from '@ledgerhq/hw-transport-webhid';
import TransportWebUSB from '@ledgerhq/hw-transport-webusb';
import { ethers } from 'ethers';
import numeral from 'numeral';
import BigNumber from 'bignumber.js';
import '@kouts/vue-modal/dist/vue-modal.css';
import axios from 'axios';
import { fetchPrices, formatUsd } from '../services/priceService';
import { saveAccount, getAccounts } from '../services/accountService';
import { getCustomTokens } from '../services/tokenService';
import { loadEvmTokens } from '../services/tokenLoader';
import { getAddresses, saveAddress, deleteAddress as deleteAddressPb } from '../services/pbService';
import ReceiveModal from './ReceiveModal.vue';
import AddressMatrix from './AddressMatrix.vue';
import { deriveEvmAddress, isValidHex } from '../services/evmXpub';
import eventBus from '../services/eventBus';

const EVM_NODE_KEY = 'hd_polygon_account_node';
const MATRIX_ADDRESS_COUNT = 5;

const CHAIN_ID = 137;
const ACCENT = '#8247E5';
const NATIVE = 'MATIC';
const ETHERSCAN_KEY = process.env.VUE_APP_ETHERSCAN_KEY || '';
const SCAN_API = `https://api.etherscan.io/v2/api?chainid=137&apikey=${ETHERSCAN_KEY}`;

const RPC_LIST = [
    { label: 'PublicNode', url: 'https://polygon.publicnode.com' },
    { label: 'Ankr',       url: 'https://rpc.ankr.com/polygon' },
    { label: 'LlamaNodes', url: 'https://polygon.llamarpc.com' },
];

const DEFAULT_TOKENS = [
    { ticker: 'USDT',  address: '0xc2132D05D31c914a87C6611C10748AEb04B58e8F', decimals: 6  },
    { ticker: 'USDC',  address: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', decimals: 6  },
    { ticker: 'WBTC',  address: '0x1BFD67037B42Cf73acF2047067bd4F2C47D9BfD6', decimals: 8  },
    { ticker: 'WETH',  address: '0x7ceB23fD6bC0adD59E62ac25578270cFf1b9f619', decimals: 18 },
    { ticker: 'DAI',   address: '0x8f3Cf7ad23Cd3CaDbD9735AFf958023239c6A063', decimals: 18 },
    { ticker: 'WMATIC', address: '0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270', decimals: 18 },
    { ticker: 'LINK',  address: '0x53E0bca35eC356BD5ddDFebbD1Fc0fD03FaBad39', decimals: 18 },
];

const ERC20_ABI = [
    'function balanceOf(address) view returns (uint256)',
    'function transfer(address to, uint256 amount) returns (bool)',
];

let provider = new ethers.providers.JsonRpcProvider(RPC_LIST[0].url);
let transport = null;
let ethApp = null;

export default {
    name: 'PolygonWallet',
    components: {
        ReceiveModal,
        Modal: () => import('@kouts/vue-modal').then(m => m.default),
        WalletTxHistory: () => import('./WalletTxHistory.vue'),
        AddressMatrix,
    },
    data() {
        return {
            maticAddress: 'N/A', accountIndex: 0, hdPath: 'N/A',
            accountNode: null, // { pubkeyHex, chainCodeHex } at m/44'/60'/0'/0
            matrixCells: [],
            matrixLoading: false,
            balances: [], tokensLoading: false, pricingProgress: null, rpcList: RPC_LIST, activeRpc: RPC_LIST[0].url, rpcStatus: {},
            showModal: false, showReceipt: false, showReceive: false,
            modalTicker: '', modalToAddress: '', modalAmount: '', modalMaxBalance: '',
            modalTxType: '', modalContractAddress: '', modalDecimals: 0,
            gasPrice: 80, gasLimit: 21000, sending: false, txid: '',
            showAddAddressModal: false, newAddressName: '', newAddress: '',
            addresses: [], prices: {}, priceTimer: null,
            chartAddress: '', chartMode: 'count', chartDays: 30,
            chartLoading: false, chartLoaded: false, chartInstance: null,
            chartStats: { total: 0, received: '0', sent: '0' },
        };
    },
    computed: {
        estimatedFee() {
            return new BigNumber(this.gasPrice).multipliedBy(this.gasLimit).div(1e9).toFixed(8);
        },
        // Native token pinned to the top row, everything else sorted by USD value descending.
        sortedBalances() {
            const native = this.balances.filter(b => b.type === 'native');
            const rest = this.balances.filter(b => b.type !== 'native')
                .slice()
                .sort((a, b) => this.usdValueNum(b) - this.usdValueNum(a));
            return [...native, ...rest];
        },
        totalUsdt() {
            return this.balances.reduce((sum, b) => sum + Math.max(this.usdValueNum(b), 0), 0);
        },
        totalUsdtStr() {
            return formatUsd(this.totalUsdt) + ' USDT';
        },
        pricingProgressMsg() {
            const p = this.pricingProgress;
            if (!p) return 'Scanning all tokens…';
            if (p.stage === 'binance') return 'Pricing via Binance…';
            if (p.stage === 'coingecko') return `Pricing ${p.current}/${p.total} tokens via CoinGecko…`;
            return 'Scanning all tokens…';
        },
    },
    methods: {
        async createTransport() {
            if (await TransportWebHID.isSupported()) return TransportWebHID.create();
            if (await TransportWebUSB.isSupported()) return TransportWebUSB.create();
            throw new Error('WebHID/WebUSB not supported. Please use Chrome or Edge over HTTPS.');
        },

        async onConnectLedger(e) {
            if (e) e.preventDefault();
            this.notice('info', 'Connecting', 'Opening Ledger connection…', 3);
            try {
                if (transport) await transport.close().catch(() => {});
                transport = await this.createTransport();
                ethApp = new Eth(transport);
                const path = `44'/60'/0'/0/${this.accountIndex}`;
                const { address } = await ethApp.getAddress(path, false, true);
                this.maticAddress = address;
                this.hdPath = 'm/' + path;
                this.notice('success', 'Connected', 'Address loaded.');
                await this.loadBalances();
                await this.fetchGasPrice();
                await this.refreshPrices();
                const accounts = saveAccount({ chain: 'polygon', hdIndex: this.accountIndex, address, label: `Polygon #${this.accountIndex}` });
                if (this.$store) this.$store.commit('accounts/SET_ACCOUNTS', accounts);

                // Cache the account-level pubkey/chaincode (m/44'/60'/0'/0) so the
                // Address Matrix/Tree can derive other indices without reconnecting.
                try {
                    const { publicKey, chainCode } = await ethApp.getAddress("44'/60'/0'/0", false, true);
                    if (isValidHex(publicKey) && isValidHex(chainCode)) {
                        this.accountNode = { pubkeyHex: publicKey, chainCodeHex: chainCode };
                        localStorage.setItem(EVM_NODE_KEY, JSON.stringify(this.accountNode));
                    }
                } catch (nodeErr) {
                    console.error('Failed to cache polygon account node for matrix/tree:', nodeErr);
                }
                await this.loadMatrix();
            } catch (err) {
                const msg = err.message || String(err);
                const friendly = msg.includes('0x6e00') || msg.includes('CLA_NOT_SUPPORTED')
                    ? 'Open the Ethereum app on your Ledger, then try again. Polygon uses the Ethereum Ledger app.'
                    : msg;
                this.notice('error', 'Ledger Error', friendly, 10);
            }
        },

        // ── Address Matrix / Tree ──────────────────────────────────────────
        async loadMatrix(notifyIfEmpty = false) {
            const cells = [];
            const savedAccounts = getAccounts().filter(a => a.chain === 'polygon');
            for (let idx = 0; idx < MATRIX_ADDRESS_COUNT; idx++) {
                let address = null;
                if (this.accountNode) {
                    try {
                        address = deriveEvmAddress(this.accountNode.pubkeyHex, this.accountNode.chainCodeHex, idx);
                    } catch (e) {
                        console.error(`Failed to derive polygon address for index ${idx}:`, e);
                    }
                } else {
                    const saved = savedAccounts.find(a => a.hdIndex === idx);
                    if (saved) address = saved.address;
                }
                if (address) cells.push({ index: idx, address, balance: null, loading: true });
            }
            this.matrixCells = cells;
            if (!cells.length) {
                if (notifyIfEmpty) {
                    this.notice('error', 'Address Matrix Empty', 'No cached addresses/xpub found — reconnect your Ledger to populate it.', 8);
                }
                return;
            }
            this.matrixLoading = true;
            const results = await Promise.allSettled(
                cells.map(async c => ({ index: c.index, wei: await provider.getBalance(c.address) }))
            );
            const byIndex = {};
            for (const r of results) {
                if (r.status === 'fulfilled') byIndex[r.value.index] = parseFloat(ethers.utils.formatEther(r.value.wei));
                else console.error('[polygon matrix] balance fetch rejected:', r.reason);
            }
            this.matrixCells = this.matrixCells.map(c => ({ ...c, balance: byIndex[c.index] ?? 0, loading: !(c.index in byIndex) }));
            this.matrixLoading = false;
        },

        onMatrixSelect(idx) {
            const cell = this.matrixCells.find(c => c.index === idx);
            if (!cell) return;
            this.accountIndex = idx;
            this.maticAddress = cell.address;
            this.hdPath = `m/44'/60'/0'/0/${idx}`;
            this.loadBalances();
        },

        async loadBalances() {
            this.balances = [];
            try {
                const wei = await provider.getBalance(this.maticAddress);
                const bal = ethers.utils.formatEther(wei);
                this.balances.push({ ticker: NATIVE, balance: numeral(parseFloat(bal)).format('0,0.000000000'), type: 'native', rawBalance: bal, contractAddress: null, decimals: 18 });
            } catch (e) { this.notice('error', 'Balance Error', e.message, 8); }

            this.tokensLoading = true;
            this.pricingProgress = null;
            loadEvmTokens(this.maticAddress, 137, provider, 'polygon', DEFAULT_TOKENS, p => { this.pricingProgress = p; })
                .then(tokens => {
                    this.balances = this.balances.filter(b => b.type !== 'erc20');
                    this.balances.push(...tokens);
                    if (this.$store) this.$store.commit('wallets/SET_WALLET', { chain: 'polygon', data: { address: this.maticAddress, balances: this.balances, connected: true, accountIndex: this.accountIndex } });
                })
                .catch(e => this.notice('error', 'Token Load Error', e.message, 6))
                .finally(() => { this.tokensLoading = false; this.pricingProgress = null; });

            if (this.$store) {
                this.$store.commit('wallets/SET_WALLET', { chain: 'polygon', data: { address: this.maticAddress, balances: this.balances, connected: true, accountIndex: this.accountIndex } });
            }
            this.notice('success', 'Balances', 'MATIC loaded — tokens loading…', 2);
        },

        async fetchGasPrice() {
            try { const gp = await provider.getGasPrice(); this.gasPrice = Math.ceil(parseFloat(ethers.utils.formatUnits(gp, 'gwei'))); } catch {}
        },

        async refreshPrices() {
            const tickers = this.balances.map(b => b.ticker);
            const { prices } = await fetchPrices(tickers);
            this.prices = prices;
            if (this.$store) this.$store.dispatch('prices/fetchPrices', tickers);
        },

        priceOf(ticker, rawBalance) {
            const raw = parseFloat(String(rawBalance).replace(/,/g, '')) || 0;
            const price = this.prices[ticker] ?? null;
            if (price === null) return '—';
            return '$' + (raw * price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },

        // Numeric USD value for sorting — falls back to live price lookup like priceOf(),
        // but returns -1 (sorts last) when no value can be determined at all.
        usdValueNum(b) {
            if (b.usdValue != null) return b.usdValue;
            const raw = parseFloat(String(b.rawBalance).replace(/,/g, '')) || 0;
            const price = this.prices[b.ticker] ?? null;
            return price === null ? -1 : raw * price;
        },

        openModal(ticker, balance, type, contractAddress, decimals) {
            this.showModal = true; this.modalTicker = ticker; this.modalToAddress = '';
            this.modalAmount = ''; this.modalMaxBalance = balance.replace(/,/g, '');
            this.modalTxType = type; this.modalContractAddress = contractAddress || '';
            this.modalDecimals = decimals || 18; this.gasLimit = type === 'erc20' ? 65000 : 21000;
            this.txid = ''; this.fetchGasPrice();
        },

        async sendTransaction() {
            if (!ethers.utils.isAddress(this.modalToAddress)) { this.notice('error', 'Invalid Address', 'Enter a valid Polygon address.', 5); return; }
            if (!this.modalAmount || parseFloat(this.modalAmount) <= 0) { this.notice('error', 'Invalid Amount', 'Amount must be > 0.', 5); return; }
            this.sending = true;
            const path = `44'/60'/0'/0/${this.accountIndex}`;
            try {
                const nonce = await provider.getTransactionCount(this.maticAddress, 'latest');
                const gasPriceWei = ethers.utils.parseUnits(String(this.gasPrice), 'gwei');
                let txData;
                if (this.modalTxType === 'native') {
                    txData = { to: this.modalToAddress, value: ethers.utils.parseEther(String(this.modalAmount)).toHexString(), gasPrice: gasPriceWei.toHexString(), gasLimit: ethers.BigNumber.from(this.gasLimit).toHexString(), nonce: ethers.utils.hexlify(nonce), chainId: CHAIN_ID, data: '0x' };
                } else {
                    const iface = new ethers.utils.Interface(ERC20_ABI);
                    const amount = new BigNumber(this.modalAmount).multipliedBy(new BigNumber(10).pow(this.modalDecimals)).toFixed(0);
                    txData = { to: this.modalContractAddress, value: '0x0', gasPrice: gasPriceWei.toHexString(), gasLimit: ethers.BigNumber.from(this.gasLimit).toHexString(), nonce: ethers.utils.hexlify(nonce), chainId: CHAIN_ID, data: iface.encodeFunctionData('transfer', [this.modalToAddress, ethers.BigNumber.from(amount)]) };
                }
                const unsignedTx = ethers.utils.serializeTransaction(txData);
                const sig = await ethApp.signTransaction(path, unsignedTx.slice(2), null);
                const _sigToHex = (val) => {
                    if (Buffer.isBuffer(val)) return ethers.utils.hexlify(val);
                    if (typeof val === 'string' && val.includes(',')) return ethers.utils.hexlify(Buffer.from(val.split(',').map(Number)));
                    if (typeof val === 'string') return ethers.utils.hexlify(Buffer.from(val, 'hex'));
                    return ethers.utils.hexlify(val);
                };
                const rHex = _sigToHex(sig.r);
                const sHex = _sigToHex(sig.s);
                const txHash = ethers.utils.keccak256(unsignedTx);
                let vInt = CHAIN_ID * 2 + 35;
                for (const candidate of [CHAIN_ID * 2 + 35, CHAIN_ID * 2 + 36]) {
                    try {
                        const recovered = ethers.utils.recoverAddress(txHash, { r: rHex, s: sHex, v: candidate });
                        if (recovered.toLowerCase() === this.maticAddress.toLowerCase()) { vInt = candidate; break; }
                    } catch {}
                }
                const signedTx = ethers.utils.serializeTransaction(txData, { v: vInt, r: rHex, s: sHex });
                const response = await provider.sendTransaction(signedTx);
                this.txid = response.hash; this.showModal = false; this.showReceipt = true;
                this.notice('success', 'Sent!', `TX: ${response.hash.slice(0, 16)}…`, 5);
                response.wait(1).then(() => this.loadBalances()).catch(() => {});
            } catch (err) { this.notice('error', 'Transaction Error', err.message || String(err), 10); }
            finally { this.sending = false; }
        },

        async pingRpc(url) {
            this.$set(this.rpcStatus, url, 'checking');
            try { const p = new ethers.providers.JsonRpcProvider(url); await p.getBlockNumber(); this.$set(this.rpcStatus, url, 'ok'); }
            catch { this.$set(this.rpcStatus, url, 'error'); }
        },
        switchRpc(url) { this.activeRpc = url; provider = new ethers.providers.JsonRpcProvider(url); this.pingRpc(url); },
        pingAllRpc() { this.rpcList.forEach(r => this.pingRpc(r.url)); },

        async fetchAddresses() {
            try {
                const res = await getAddresses();
                this.addresses = res.items
                    .filter(r => r.chain === 'polygon' || r.chain === 'all')
                    .map(r => ({ id: r.id, name: r.label, address: r.address }));
            } catch (e) { console.error('Failed to load addresses:', e); }
        },

        async submitNewAddress() {
            if (!ethers.utils.isAddress(this.newAddress)) { alert('Enter a valid Polygon address.'); return; }
            try {
                await saveAddress({
                    label: this.newAddressName,
                    address: this.newAddress,
                    chain: 'polygon',
                    notes: '',
                    aml_risk_level: 'unknown',
                });
                await this.fetchAddresses();
            } catch (e) {
                alert('Failed to add address: ' + (e?.message || e));
                return;
            }
            this.newAddressName = ''; this.newAddress = ''; this.showAddAddressModal = false;
        },

        async deleteAddress(index) {
            const entry = this.addresses[index];
            try {
                if (entry.id) await deleteAddressPb(entry.id);
                this.addresses.splice(index, 1);
            } catch (e) {
                alert('Delete failed: ' + (e?.message || e));
            }
        },
        copyAddress(address) { navigator.clipboard.writeText(address).then(() => this.notice('success', 'Copied', 'Address copied.', 2)); },
        logout() { localStorage.removeItem('isLoggedIn'); window.location.reload(); },
        notice(type, title, description, duration) { this.$notice[type]({ title, description: description || '', duration: duration ?? 1 }); },

        setChartMode(mode) { this.chartMode = mode; if (this.chartLoaded) this.renderChart(); },

        async loadChart() {
            const addr = this.chartAddress.trim();
            if (!ethers.utils.isAddress(addr)) { this.notice('error', 'Invalid Address', 'Enter a valid 0x Polygon address.', 4); return; }
            this.chartLoading = true; this.chartLoaded = false;
            try {
                const url = `${SCAN_API}&module=account&action=txlist&address=${addr}&sort=desc&page=1&offset=200&startblock=0&endblock=99999999`;
                const res = await axios.get(url, { timeout: 15000 });
                const txs = Array.isArray(res.data.result) ? res.data.result : [];
                const now = Date.now(); const cutoff = now - this.chartDays * 86400000;
                const countMap = {}, sentMap = {}, recvMap = {};
                for (let d = 0; d < this.chartDays; d++) { const day = this._dayKey(now - d * 86400000); countMap[day] = 0; sentMap[day] = 0; recvMap[day] = 0; }
                let totalCount = 0, totalSent = 0, totalRecv = 0;
                txs.forEach(tx => {
                    if (tx.isError === '1') return;
                    const ts = parseInt(tx.timeStamp) * 1000;
                    if (ts < cutoff) return;
                    const day = this._dayKey(ts);
                    if (!(day in countMap)) return;
                    countMap[day]++; totalCount++;
                    const val = parseFloat(ethers.utils.formatEther(tx.value || '0'));
                    if (tx.from.toLowerCase() === addr.toLowerCase()) { sentMap[day] += val; totalSent += val; }
                    if (tx.to.toLowerCase() === addr.toLowerCase()) { recvMap[day] += val; totalRecv += val; }
                });
                this.chartStats = { total: totalCount, sent: totalSent.toFixed(6), received: totalRecv.toFixed(6) };
                this._chartData = { labels: Object.keys(countMap).sort(), countMap, sentMap, recvMap };
                this.chartLoaded = true;
                this.$nextTick(() => this.renderChart());
            } catch (e) { this.notice('error', 'Chart Error', e.message || String(e), 10); }
            finally { this.chartLoading = false; }
        },

        _dayKey(ms) { const d = new Date(ms); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; },

        renderChart() {
            if (!this._chartData) return;
            const { labels, countMap, sentMap, recvMap } = this._chartData;
            if (this.chartInstance) { this.chartInstance.destroy(); this.chartInstance = null; }
            const ctx = this.$refs.txChart; if (!ctx) return;
            const shortLabels = labels.map(l => l.slice(5));
            let datasets;
            if (this.chartMode === 'count') {
                datasets = [{ label: 'TX Count', data: labels.map(l => countMap[l]), backgroundColor: 'rgba(130,71,229,0.75)', borderColor: ACCENT, borderWidth: 1, borderRadius: 3 }];
            } else {
                datasets = [
                    { label: 'MATIC Received', data: labels.map(l => parseFloat(recvMap[l].toFixed(6))), backgroundColor: 'rgba(40,167,69,0.75)', borderColor: '#28a745', borderWidth: 1, borderRadius: 3 },
                    { label: 'MATIC Sent', data: labels.map(l => parseFloat(sentMap[l].toFixed(6))), backgroundColor: 'rgba(130,71,229,0.65)', borderColor: ACCENT, borderWidth: 1, borderRadius: 3 },
                ];
            }
            this.chartInstance = new Chart(ctx, {
                type: 'bar', data: { labels: shortLabels, datasets },
                options: { responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, plugins: { legend: { position: 'top' } }, scales: { x: { stacked: this.chartMode === 'volume', grid: { display: false }, ticks: { maxRotation: 45, autoSkip: true, maxTicksLimit: 15 } }, y: { stacked: this.chartMode === 'volume', beginAtZero: true } } },
            });
        },
    },
    async mounted() {
        await this.fetchAddresses();
        this.pingAllRpc();
        this.priceTimer = setInterval(() => this.refreshPrices(), 60000);
        eventBus.$on('refresh-prices', this.refreshPrices);
        const active = localStorage.getItem('hd_active_polygon');
        if (active) { const accts = JSON.parse(localStorage.getItem('hd_accounts') || '[]'); const a = accts.find(x => x.id === active); if (a) this.accountIndex = a.hdIndex; }

        try {
            const cached = JSON.parse(localStorage.getItem(EVM_NODE_KEY) || 'null');
            if (cached && isValidHex(cached.pubkeyHex) && isValidHex(cached.chainCodeHex)) {
                this.accountNode = cached;
            }
        } catch (e) {
            console.error('Failed to load cached polygon account node:', e);
        }
        this.loadMatrix();
    },
    beforeDestroy() { clearInterval(this.priceTimer); eventBus.$off('refresh-prices', this.refreshPrices); if (this.chartInstance) this.chartInstance.destroy(); },
};
</script>

<style scoped>
/* ── Polygon brand ── */
.btn-matic { background: rgba(130,71,229,0.18) !important; border: 1px solid rgba(130,71,229,0.5) !important; color: #c4b5fd !important; font-weight: 600 !important; }
.btn-matic:hover, .btn-matic:focus { background: rgba(130,71,229,0.32) !important; color: #fff !important; }
.table thead th { background: rgba(130,71,229,0.15) !important; color: #c4b5fd !important; border-bottom: 2px solid rgba(130,71,229,0.4) !important; }
.ticker-badge { display: inline-block; background: rgba(130,71,229,0.18); color: #c4b5fd; font-size: 0.7rem; font-weight: 700; padding: 2px 8px; border-radius: 8px; border: 1px solid rgba(130,71,229,0.35); }
.rpc-panel { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 10px 14px; background: var(--surface, #111520); border-radius: 10px; border: 1px solid var(--border, #242a3a); margin-bottom: 4px; }
.rpc-title { font-size: 0.7rem; font-weight: 700; color: var(--text-muted, #52597a); text-transform: uppercase; letter-spacing: 0.07em; }
.rpc-item { display: flex; align-items: center; gap: 5px; font-size: 0.78rem; font-weight: 500; padding: 4px 11px; border-radius: 20px; cursor: pointer; border: 1px solid var(--border, #242a3a); background: var(--surface-2, #171b28); color: var(--text, #c8cfe8); transition: all 0.18s; }
.rpc-item:hover { border-color: #8247E5; background: rgba(130,71,229,0.08); }
.rpc-active { border-color: #8247E5 !important; background: rgba(130,71,229,0.14) !important; color: #fff !important; font-weight: 700 !important; }
.rpc-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--border-2, #2e3650); display: inline-block; flex-shrink: 0; }
.rpc-dot.ok { background: #22c55e; } .rpc-dot.error { background: #f43f5e; } .rpc-dot.checking { background: #f59e0b; }
.chart-panel { border: 1px solid var(--border, #242a3a); border-radius: 12px; padding: 18px; background: var(--surface, #111520); box-shadow: 0 4px 24px rgba(0,0,0,0.4); }
.chart-toolbar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.chart-panel-title { font-weight: 700; font-size: 0.86rem; color: var(--text, #c8cfe8); white-space: nowrap; margin-right: 6px; }
.chart-input-group { display: flex; gap: 6px; flex: 1; min-width: 200px; }
.chart-addr-input { flex: 1; font-size: 0.82rem; }
.chart-toggles { display: flex; gap: 4px; }
.chart-summary { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 10px; font-size: 0.8rem; padding: 8px 12px; background: var(--surface-2, #171b28); border-radius: 8px; }
.chart-stat { display: flex; align-items: center; gap: 4px; }
.stat-in { color: #22c55e; font-weight: 700; } .stat-out { color: #8247E5; font-weight: 700; }
.chart-wrapper { position: relative; height: 280px; }
.chart-placeholder { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; color: var(--text-muted, #52597a); font-size: 0.88rem; background: rgba(11,13,20,0.88); border-radius: 10px; }
</style>
